import { FC } from "react"
import { useGetActivitiesQuery } from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { ActivitySummaryCard } from "../atoms/ActivitySummaryCard"
import NumberIcon from "../atoms/NumberIcon"
import { createStyles, makeStyles } from "@material-ui/core"
import { VirasafeMetrics } from "../../utils/types"

/**
 * InsightSummaryActivityView - Web version
 */
export const InsightSummaryActivityView: FC<{
  insights: {
    metrics: VirasafeMetrics | null
    numTopEngagedActivities: number | null
    numLeastDistressedActivities: number | null
  }
  title: string
  subtitle: string
  showEngagingActivities?: boolean
}> = ({ insights, title, subtitle, showEngagingActivities = false }) => {
  const client = useGraphQLClient()
  const { data } = useGetActivitiesQuery(client)
  const classes = useStyles()

  const activityKeys = showEngagingActivities
    ? [
        "top_engaged_activity_1",
        "top_engaged_activity_2",
        "top_engaged_activity_3",
        "top_engaged_activity_4",
      ]
    : [
        "least_distressed_activity_1",
        "least_distressed_activity_2",
        "least_distressed_activity_3",
      ]

  return (
    <div className={classes.container}>
      <div className={classes.innerContainer}>
        <div className={classes.row}>
          <span className={classes.title}>{title}</span>
          <div className="mx-3">
            {((showEngagingActivities &&
              insights.numTopEngagedActivities !== null) ||
              (!showEngagingActivities &&
                insights.numLeastDistressedActivities !== null)) && (
              <NumberIcon
                number={
                  showEngagingActivities
                    ? insights.numTopEngagedActivities ?? 0
                    : insights.numLeastDistressedActivities ?? 0
                }
                textColor="#007546"
                backgroundColor="#EAFFF7"
              />
            )}
          </div>
        </div>
        <span className={classes.subtitle}>{subtitle}</span>
      </div>
      <div className={classes.content}>
        {activityKeys.map((activityKey) => {
          const activityId =
            insights.metrics?.[activityKey as keyof VirasafeMetrics]
          const activity = data?.activity?.find(
            (act: { id: string | number | undefined }) => act.id === activityId
          )
          return (
            activity && (
              <ActivitySummaryCard
                key={activityKey}
                activity={activity}
                isEngageActivity={showEngagingActivities}
              />
            )
          )
        })}
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    title: {
      fontWeight: 600,
      fontSize: 18,
      color: "#0B1950",
    },
    subtitle: {
      fontSize: 16,
      color: "#545E78",
      fontWeight: 400,
    },
    innerContainer: {
      marginBottom: theme.spacing(2),
    },
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
    },
    content: {
      height: "100%",
      gap: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
    },
  })
)
